import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle2, Calendar, Clock, MapPin, Mail, Phone } from 'lucide-react';

const Confirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { booking, sessionId } = location.state || {};

  if (!booking) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>No Confirmation Found</CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">
              Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold mb-3 text-gray-900">Booking Confirmed!</h1>
          <p className="text-lg text-gray-600">Thank you for choosing A Team Cleaning Service</p>
        </div>

        <Card className="border-2 mb-6">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-yellow-50">
            <CardTitle className="text-2xl">Booking Details</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-6">
              {booking.date && (
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Service Date</p>
                    <p className="font-semibold text-lg">{booking.date}</p>
                  </div>
                </div>
              )}
              
              {booking.time && (
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Time</p>
                    <p className="font-semibold text-lg">{booking.time}</p>
                  </div>
                </div>
              )}

              {booking.address && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Service Address</p>
                    <p className="font-semibold">{booking.address}</p>
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <p className="text-sm text-gray-600 mb-2">Service Details</p>
                <p className="font-semibold text-lg mb-1">{booking.service || 'Cleaning Service'}</p>
                {booking.bedrooms && booking.bathrooms && booking.sqft && (
                  <div className="text-gray-700 space-y-1 mt-2">
                    <p>• {booking.bedrooms} bedroom(s)</p>
                    <p>• {booking.bathrooms} bathroom(s)</p>
                    <p>• {booking.sqft} square feet</p>
                  </div>
                )}
                <p className="text-2xl font-bold text-blue-600 mt-3">${booking.price}</p>
              </div>

              {booking.notes && (
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 mb-1">Additional Notes</p>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded">{booking.notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 mb-6">
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {booking.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-semibold">{booking.email}</p>
                  </div>
                </div>
              )}
              {booking.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-semibold">{booking.phone}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
          <h3 className="font-semibold text-lg mb-3 text-gray-900">What's Next?</h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>A confirmation email has been sent to {booking.email}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>Our team will contact you 24 hours before the scheduled service</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>Please ensure someone is available to provide access to the property</span>
            </li>
          </ul>
        </div>

        {sessionId && (
          <div className="text-center mb-6">
            <p className="text-sm text-gray-600">Transaction ID: {sessionId}</p>
          </div>
        )}

        <div className="flex gap-4">
          <Button 
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => navigate('/')}
          >
            Return Home
          </Button>
          <Button 
            variant="outline"
            className="flex-1 border-2 border-blue-600 text-blue-600 hover:bg-blue-50"
            onClick={() => navigate('/booking')}
          >
            Book Another Service
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Confirmation;